/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */
import { NotFoundError } from '@openphone/internal-api-client'
import { action, makeAutoObservable } from 'mobx'

import makePersistable from '@src/service/storage/makePersistable'
import type { Role } from '@src/service/transport/account'

import type Service from '.'
import EntityPhoneNumberStore from './entity-phone-number-store'
import type { CodableOrganization } from './model'
import { OrganizationModel } from './model'
import { SystemUserAiAgentModel } from './model/system-users/SystemUserAiAgentModel'

export default class OrganizationStore {
  /**
   * Prefer using `OrganizationStore.getCurrentOrganization()` instead if you expect the current
   * organization to be ready.
   */
  current: OrganizationModel | null = null
  role: Role[] = []
  phoneNumber: EntityPhoneNumberStore
  aiAgent: SystemUserAiAgentModel | null = null

  constructor(private root: Service) {
    this.phoneNumber = new EntityPhoneNumberStore(root)

    makeAutoObservable(this, {})

    makePersistable(this, 'OrganizationStore', {
      role: root.storage.async(),
      current: root.storage.async((json: CodableOrganization) =>
        new OrganizationModel(this).deserialize(json),
      ),
    })
  }

  /**
   * Workspaces only get an AI agent system user once Sona is set up, so a 404 means "no AI agent
   * yet", not a failure. Any other error is still thrown.
   */
  async fetchAiAgentSystemUser() {
    try {
      const aiAgent = await this.root.transport.account.systemUser.aiAgent()

      this.aiAgent = new SystemUserAiAgentModel(aiAgent)
    } catch (error) {
      if (error instanceof NotFoundError) {
        this.aiAgent = null
        return
      }

      throw error
    }
  }

  /**
   * Returns the current organization if it's ready, otherwise throws an error.
   *
   * @returns The current organization
   * @throws If the current organization is not ready yet
   */
  getCurrentOrganization() {
    const currentOrganization = this.current

    if (!currentOrganization) {
      throw new Error(
        'OrganizationStore.getCurrentOrganization() called before the current organization was ready',
      )
    }

    return currentOrganization
  }

  update(value: CodableOrganization) {
    return this.root.transport.account.organization.update(value)
  }

  fetch() {
    return this.root.transport.account.organization.list().then(
      action((orgs) => {
        if (this.current) {
          // @ts-expect-error unchecked index access
          this.current.deserialize(orgs[0])
        } else {
          // @ts-expect-error unchecked index access
          this.current = new OrganizationModel(this).deserialize(orgs[0])
        }
      }),
    )
  }

  fetchRoles() {
    return this.root.transport.account.organization.roles().then(
      action((roles) => {
        this.role = roles.sort((a, b) => roleSortWeight(a) - roleSortWeight(b))
      }),
    )
  }

  async fetchInvites() {
    if (!this.current) {
      return []
    }

    return this.root.transport.account.organization.for(this.current.id).invites.list()
  }

  async getInvite(userId: string) {
    const invites = await this.fetchInvites()
    const invite = invites.find((invite) => invite.id === userId)
    return invite
  }
}

const roleSortWeight = (role: Role): number => {
  switch (role.name) {
    case 'owner':
      return 0
    case 'admin':
      return 1
    case 'member':
      return 2
  }
}
